import { OnboardingExtraFeature, PolicyType } from "shared";
import { CheckIcon } from "@heroicons/react/20/solid";

import { classNames } from "../../../../common/lib/classNames";

export type OnboardingFeature = PolicyType | OnboardingExtraFeature;

export interface FeaturesProps {
  chosenFeatures: OnboardingFeature[];
  setChosenFeatures: (features: OnboardingFeature[]) => void;
}

interface IFeature {
  type: OnboardingFeature;
  title: string;
  description: string;
}

const GROUPS: { heading: string; features: IFeature[] }[] = [
  {
    heading: `Reminders & statements`,
    features: [
      {
        type: PolicyType.REMINDER,
        title: `Email Reminders`,
        description: `Automatically send reminders and follow-ups for invoices using customisable email templates`,
      },
      {
        type: PolicyType.STATEMENT,
        title: `Monthly Statements`,
        description: `Automatically send statements to customers on a recurring weekly or monthly basis`,
      },
      {
        type: PolicyType.SMS,
        title: `SMS Reminders`,
        description: `Automatically send SMS reminders to your customers`,
      },
      {
        type: PolicyType.QUOTE_EMAIL,
        title: `Quote Reminders`,
        description: `Automatically follow up on sent quotes and estimates before they expire`,
      },
    ],
  },
  {
    heading: `Fees & incentives`,
    features: [
      {
        type: PolicyType.LATE_FEE,
        title: `Invoice Late Fees`,
        description: `Automatically charge late fees on each overdue invoice when they reach a chosen number of days overdue.`,
      },
      {
        type: PolicyType.STATEMENT_LATE_FEE,
        title: `Customer Late Fees`,
        description: `Automatically charge interest or fees on overdue invoices, consolidated per customer on a weekly or monthly basis.`,
      },
      {
        type: PolicyType.DISCOUNT,
        title: `Prompt Payment Discounts`,
        description: `Automatically apply discounts to invoices which will be removed if not paid within the chosen time frame`,
      },
    ],
  },
  {
    heading: `Escalations & other`,
    features: [
      {
        type: PolicyType.ESCALATION,
        title: `Schedule Calls and Escalations`,
        description: `Automatically escalate overdue invoices and schedule manual follow ups, such as phone calls or letters`,
      },
      {
        type: OnboardingExtraFeature.ONE_OFF,
        title: `Manual Actions`,
        description: `I want to manually perform one-off actions on select customers or invoices`,
      },
      {
        type: OnboardingExtraFeature.OTHER,
        title: `Something else`,
        description: `I'm after something not listed here`,
      },
      {
        type: OnboardingExtraFeature.NONE,
        title: `No automations yet`,
        description: `I just want to explore Paidnice first - I'll set up automations later`,
      },
    ],
  },
];

/** Invoice late fees and customer late fees are mutually exclusive; "no automations yet" excludes everything */
function exclusiveWith(feature: OnboardingFeature): OnboardingFeature[] {
  if (feature === PolicyType.LATE_FEE) return [PolicyType.STATEMENT_LATE_FEE, OnboardingExtraFeature.NONE];
  if (feature === PolicyType.STATEMENT_LATE_FEE) return [PolicyType.LATE_FEE, OnboardingExtraFeature.NONE];
  if (feature === OnboardingExtraFeature.NONE) return [...Object.values(PolicyType), OnboardingExtraFeature.ONE_OFF, OnboardingExtraFeature.OTHER];

  return [OnboardingExtraFeature.NONE];
}

export function Features({
  chosenFeatures,
  setChosenFeatures,
}: FeaturesProps) {
  function toggle(feature: IFeature) {
    if (chosenFeatures.includes(feature.type)) {
      setChosenFeatures(chosenFeatures.filter(f => f !== feature.type));

      return;
    }

    const exclusive = exclusiveWith(feature.type);

    setChosenFeatures([...chosenFeatures.filter(f => !exclusive.includes(f)), feature.type]);
  }

  return (
    <div className={ `flex flex-col gap-4` }>
      { GROUPS.map(group => (
        <div key={ group.heading }>
          <div className={ `mb-2.5 text-xs font-bold uppercase tracking-[0.1em] text-gray-400` }>{ group.heading }</div>
          <div className={ `grid gap-2.5 md:grid-cols-2` }>
            { group.features.map(feature => {
              const isSelected = chosenFeatures.includes(feature.type);

              return (
                <button
                  key={ feature.title }
                  type={ `button` }
                  className={ classNames(
                    `flex cursor-pointer gap-3 rounded-[10px] border-[1.5px] p-3 text-left transition-colors focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500`,
                    isSelected ? `border-paidniceGreen-600 bg-paidniceGreen-50` : `border-gray-200 hover:border-paidniceGreen-300`,
                  ) }
                  aria-pressed={ isSelected }
                  onClick={ () => toggle(feature) }
                >
                  <span className={ classNames(
                    `mt-px grid h-5 w-5 shrink-0 place-items-center rounded-md`,
                    isSelected ? `bg-paidniceGreen-600 text-white` : `border-[1.5px] border-gray-300 bg-white`,
                  ) }>
                    { isSelected && <CheckIcon className={ `h-3.5 w-3.5` }
                      aria-hidden={ true } /> }
                  </span>
                  <span>
                    <span className={ classNames(
                      `block text-sm`,
                      isSelected ? `font-bold text-paidniceGreen-800` : `font-semibold text-gray-700`,
                    ) }>
                      { feature.title }
                    </span>
                    <span className={ classNames(
                      `mt-0.5 block text-[12.5px] leading-normal`,
                      isSelected ? `text-gray-600` : `text-gray-400`,
                    ) }>
                      { feature.description }
                    </span>
                  </span>
                </button>
              );
            }) }
          </div>
        </div>
      )) }
    </div>
  );
}
